/**
 * Single source of truth for the names of the four Operations surfaces that used to carry different names in the
 * sidebar, the ops ribbon, the page header and the help text. The pages stay separate; only the label converges.
 * Canonical names (Astra PTUX-004): Today, Reply Review, Customer Requests, Team Dispatch.
 */

export type OpsSurfaceId = 'today' | 'reply-review' | 'customer-requests' | 'team-dispatch';

export interface OpsSurfaceLabel {
  id: OpsSurfaceId;
  /** The one name staff see everywhere. */
  label: string;
  /** What the page is for, in one line. */
  job: string;
  /** Who normally works in it. */
  audience: string;
  href: string;
}

export const OPS_SURFACE_LABELS: Readonly<Record<OpsSurfaceId, OpsSurfaceLabel>> = Object.freeze({
  today: {
    id: 'today',
    label: 'Today',
    job: 'Daily priorities, desk coverage and what needs doing now',
    audience: 'Every consultant',
    href: '/ops/today',
  },
  'reply-review': {
    id: 'reply-review',
    label: 'Reply Review',
    job: 'Client threads waiting on a reply or a review of a drafted reply',
    audience: 'Consultants and supervisors',
    href: '/ops/reply-queue',
  },
  'customer-requests': {
    id: 'customer-requests',
    label: 'Customer Requests',
    job: 'Post-booking customer care requests and special assistance from every channel',
    audience: 'Customer care',
    href: '/ops/customer-requests',
  },
  'team-dispatch': {
    id: 'team-dispatch',
    label: 'Team Dispatch',
    job: 'Assign unowned matters and inspect each consultant\'s workload and focus',
    audience: 'Supervisors',
    href: '/ops/dispatch',
  },
});

/** Convenience lookup by route, for nav surfaces that already know the href. */
export function opsSurfaceLabelForHref(href: string): OpsSurfaceLabel | undefined {
  return Object.values(OPS_SURFACE_LABELS).find((surface) => surface.href === href);
}
